import client, { PAGE_SIZE } from "./client";

/**
 * One module per resource would be six files of three lines each; grouping them
 * by domain keeps the API surface readable in one place. Every function returns
 * the response body directly - callers care about data, not envelopes.
 */

const body = (promise) => promise.then((response) => response.data);

// --- auth & account ---

export const auth = {
  register: (payload) => body(client.post("/users", payload)),
  login: (email, password) => body(client.post("/users/login", { email, password })),
  me: () => body(client.get("/users/me")),
  updateProfile: (payload) => body(client.put("/users/me", payload)),
  updatePicture: (picture, x, y) => body(client.put("/users/me/picture", { picture, x, y })),
  forgotPassword: (email) => body(client.post("/users/forgot-password", { email })),
  resetPassword: (payload) => body(client.post("/users/reset-password", payload)),
  search: (q) => body(client.get("/users/search", { params: { q } })),
  config: () => body(client.get("/config")),
};

export const mfa = {
  status: () => body(client.get("/users/me/mfa")),
  totpSetup: () => body(client.post("/users/me/mfa/totp/setup")),
  totpConfirm: (code) => body(client.post("/users/me/mfa/totp/confirm", { code })),
  totpDisable: () => body(client.delete("/users/me/mfa/totp")),
  webauthnRegisterBegin: () => body(client.post("/users/me/mfa/webauthn/begin")),
  webauthnRegisterFinish: (payload) => body(client.post("/users/me/mfa/webauthn/finish", payload)),
  webauthnDelete: (credentialId) => body(client.delete(`/users/me/mfa/webauthn/${credentialId}`)),
  loginTotp: (challengeToken, code) => body(client.post("/users/login/mfa/totp", { challengeToken, code })),
  loginWebauthnBegin: (challengeToken) => body(client.post("/users/login/mfa/webauthn/begin", { challengeToken })),
  loginWebauthnFinish: (payload) => body(client.post("/users/login/mfa/webauthn/finish", payload)),
};

// --- clubs ---

export const clubs = {
  list: () => body(client.get("/clubs")),
  listAll: () => body(client.get("/admin/clubs")),
  create: (payload) => body(client.post("/clubs", payload)),
  get: (clubId) => body(client.get(`/clubs/${clubId}`)),
  update: (clubId, payload) => body(client.put(`/clubs/${clubId}`, payload)),
  remove: (clubId) => body(client.delete(`/clubs/${clubId}`)),
  members: (clubId) => body(client.get(`/clubs/${clubId}/members`)),
  addMember: (clubId, payload) => body(client.post(`/clubs/${clubId}/members`, payload)),
  setMemberRole: (clubId, userId, role) => body(client.put(`/clubs/${clubId}/members/${userId}`, { role })),
  removeMember: (clubId, userId) => body(client.delete(`/clubs/${clubId}/members/${userId}`)),
  leave: (clubId) => body(client.delete(`/clubs/${clubId}/members/me`)),
  transfer: (clubId, userId) => body(client.post(`/clubs/${clubId}/transfer`, { userId })),
  feed: (clubId, page = 0) => body(client.get(`/clubs/${clubId}/feed`, { params: { page, size: PAGE_SIZE } })),
  feedback: (clubId, page = 0) => body(client.get(`/clubs/${clubId}/feedback`, { params: { page, size: PAGE_SIZE } })),
};

// --- exercises & 1RMs ---

export const exercises = {
  list: (q) => body(client.get("/exercises", { params: q ? { q } : {} })),
  create: (payload) => body(client.post("/exercises", payload)),
  update: (exerciseId, payload) => body(client.put(`/exercises/${exerciseId}`, payload)),
  // What a delete would take with it, so the superadmin confirms an informed
  // cascade rather than discovering it afterwards.
  usage: (exerciseId) => body(client.get(`/exercises/${exerciseId}/usage`)),
  remove: (exerciseId) => body(client.delete(`/exercises/${exerciseId}`)),
};

export const oneRms = {
  list: () => body(client.get("/one-rms")),
  set: (exerciseId, value) => body(client.put(`/one-rms/${exerciseId}`, { value })),
  remove: (exerciseId) => body(client.delete(`/one-rms/${exerciseId}`)),
};

// --- programs ---

/**
 * Where a program lives.
 *
 * A club's programs hang off the club; a program somebody wrote for themselves
 * hangs off nothing, and its routes are the same handlers with a different rule
 * about who may reach them. One helper rather than a second client, so a screen
 * can work on either kind by passing a club id or not passing one.
 */
const programsBase = (clubId) => (clubId ? `/clubs/${clubId}/programs` : "/programs");

/**
 * The outside accounts that count a member's steps for them.
 *
 * Provider-keyed rather than named after one integration: Google Health is
 * where Fitbit data arrives now that the Fitbit Web API is switched off, and
 * that sentence has had a different subject before.
 */
export const devices = {
  list: () => body(client.get("/users/me/devices")),
  // The consent URL, not a redirect: the caller decides whether that opens in
  // this tab or a new one.
  authorizeUrl: (provider) => body(client.get(`/users/me/devices/${provider}/authorize`)),
  disconnect: (provider) => body(client.delete(`/users/me/devices/${provider}`)),
  // range is one of 24h, 48h, week, month, 90d - the API names the windows so
  // the clients cannot disagree about what "a month" means.
  steps: (provider, range) =>
    body(client.get(`/users/me/devices/${provider}/steps`, { params: { range } })),
  heartRate: (provider, range) =>
    body(client.get(`/users/me/devices/${provider}/heart-rate`, { params: { range } })),
  // The latest reading of each measure the scale takes; no range, since the
  // panel is "where am I now".
  bodyComposition: (provider) => body(client.get(`/users/me/devices/${provider}/body-composition`)),
  // format is "pdf" or "png": the route's own extension, so the file that
  // arrives is named the way its contents are.
  export: (provider, { format = "png", range, locale } = {}) =>
    client
      .get(`/users/me/devices/${provider}/export.${format}`, {
        params: { ...(range ? { range } : {}), ...(locale ? { locale } : {}) },
        responseType: "blob",
      })
      .then((response) => response.data),
  // What the AI made of the last ninety days. Kept, and askable again once a
  // day - the numbers underneath move that often at most.
  healthFeedback: (provider) => body(client.get(`/users/me/devices/${provider}/feedback`)),
  generateHealthFeedback: (provider, connectionId) =>
    body(client.post(`/users/me/devices/${provider}/feedback`, null, {
      params: connectionId ? { connectionId } : {},
    })),
  deleteHealthFeedback: (provider) => body(client.delete(`/users/me/devices/${provider}/feedback`)),
  // Account-paired providers (Stance): exchange an email and password for a
  // grant. The password is used once and never stored - only the tokens are.
  pair: (provider, email, password) =>
    body(client.post(`/users/me/devices/${provider}/pair`, { email, password })),
  // Account-paired providers connected with Google: the client obtains a
  // Google ID token (public client id) and sends it here; only the resulting
  // session is stored.
  pairGoogle: (provider, idToken) =>
    body(client.post(`/users/me/devices/${provider}/google`, { idToken })),
  // Key-paired providers (Garmin): the member enters their own personal API
  // key, exchanged for a grant kept on the server; the key is never echoed.
  pairKey: (provider, apiKey) =>
    body(client.post(`/users/me/devices/${provider}/pair-key`, { apiKey })),
  // The last set of each lift a bar-speed provider tracked, with the RPE it
  // computed from the bar's speed.
  barSpeed: (provider) => body(client.get(`/users/me/devices/${provider}/bar-speed`)),
};

export const programs = {
  // Programs of a club, or - with no club - the ones the caller wrote for
  // themselves.
  list: (clubId) => body(client.get(programsBase(clubId))),
  create: (clubId, payload) => body(client.post(programsBase(clubId), payload)),
  get: (clubId, programId, memberId) =>
    body(client.get(`${programsBase(clubId)}/${programId}`, { params: memberId ? { memberId } : {} })),
  update: (clubId, programId, payload) => body(client.put(`${programsBase(clubId)}/${programId}`, payload)),
  remove: (clubId, programId) => body(client.delete(`${programsBase(clubId)}/${programId}`)),
  importFile: (clubId, file, name, description) => {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    if (description) form.append("description", description);
    return body(client.post(`${programsBase(clubId)}/import`, form));
  },
  // Duplicates the program into another club, or into the caller's own
  // library with a blank clubId. `move` transfers it instead.
  copy: (clubId, programId, payload) =>
    body(client.post(`${programsBase(clubId)}/${programId}/copy`, payload)),
  // The printable sheet, a page per week. Fetched as a blob rather than
  // linked, because the request carries the session's Authorization header -
  // a plain <a href> would arrive logged out.
  // format is "pdf" or "xlsx": the route's own extension, so the file that
  // arrives is named the way its contents are.
  export: (clubId, programId, { format = "pdf", memberId, locale } = {}) =>
    client
      .get(`${programsBase(clubId)}/${programId}/export.${format}`, {
        params: { ...(memberId ? { memberId } : {}), ...(locale ? { locale } : {}) },
        responseType: "blob",
      })
      .then((response) => response.data),
  addDay: (clubId, programId, payload) => body(client.post(`${programsBase(clubId)}/${programId}/days`, payload)),
  updateDay: (clubId, programId, dayId, payload) =>
    body(client.put(`${programsBase(clubId)}/${programId}/days/${dayId}`, payload)),
  removeDay: (clubId, programId, dayId) =>
    body(client.delete(`${programsBase(clubId)}/${programId}/days/${dayId}`)),
  // week 0 means the week the session is already in (a reorder); position is
  // the 1-based place among that week's sessions.
  moveDay: (clubId, programId, dayId, week, position) =>
    body(client.put(`${programsBase(clubId)}/${programId}/days/${dayId}/move`, { week, position })),
  duplicateDay: (clubId, programId, dayId) =>
    body(client.post(`${programsBase(clubId)}/${programId}/days/${dayId}/duplicate`)),
  addSet: (clubId, programId, dayId, payload) =>
    body(client.post(`${programsBase(clubId)}/${programId}/days/${dayId}/sets`, payload)),
  updateSet: (clubId, programId, setId, payload) =>
    body(client.put(`${programsBase(clubId)}/${programId}/sets/${setId}`, payload)),
  removeSet: (clubId, programId, setId) =>
    body(client.delete(`${programsBase(clubId)}/${programId}/sets/${setId}`)),
  // dayId may be the set's own session (a reorder) or another one (a move).
  // Position is 1-based; the API clamps it rather than refusing a drop past
  // the end of a list.
  moveSet: (clubId, programId, setId, dayId, position) =>
    body(client.put(`${programsBase(clubId)}/${programId}/sets/${setId}/move`, { dayId, position })),
  duplicateSet: (clubId, programId, setId) =>
    body(client.post(`${programsBase(clubId)}/${programId}/sets/${setId}/duplicate`)),
  assignments: (clubId, programId) => body(client.get(`${programsBase(clubId)}/${programId}/assignments`)),
  assign: (clubId, programId, payload) =>
    body(client.post(`${programsBase(clubId)}/${programId}/assignments`, payload)),
  unassign: (clubId, programId, assignmentId) =>
    body(client.delete(`${programsBase(clubId)}/${programId}/assignments/${assignmentId}`)),
};
// --- the member's own training ---

/**
 * The AI assistant: the connections a member holds to a model, the threads of
 * what they asked it, and a reading of one training session.
 *
 * Connections are shaped on storages down to the sharing, because they are the
 * same arrangement: an account holds a credential and decides who else may
 * spend it.
 */
export const ai = {
  providers: () => body(client.get("/ai/providers")),
  connections: () => body(client.get("/users/me/ai-connections")),
  addConnection: (payload) => body(client.post("/users/me/ai-connections", payload)),
  setConnection: (connectionId, payload) =>
    body(client.put(`/users/me/ai-connections/${connectionId}`, payload)),
  clearConnection: (connectionId) => body(client.delete(`/users/me/ai-connections/${connectionId}`)),
  // Which one is first decides which subscription answers when nobody picks,
  // so the order is sent as a whole list - one way for it to be wrong rather
  // than two.
  reorderConnections: (connectionIds) =>
    body(client.put("/users/me/ai-connections/order", { connectionIds })),
  shares: (connectionId) => body(client.get(`/users/me/ai-connections/${connectionId}/shares`)),
  share: (connectionId, userId) =>
    body(client.post(`/users/me/ai-connections/${connectionId}/shares`, { userId, role: "user" })),
  unshare: (connectionId, userId) =>
    body(client.delete(`/users/me/ai-connections/${connectionId}/shares/${userId}`)),

  // Asking. conversationId continues a stored thread; left out, the answer
  // starts one and comes back with its id.
  // connectionId says which subscription to spend; blank is the member's
  // default, which is what somebody with one key always wants.
  // connectionId says which subscription answers - picked at the moment of
  // sending, so one thread can run on two keys. A subscription points at its
  // own model; there is nothing else to choose.
  ask: (message, conversationId, connectionId) =>
    body(client.post("/ai/chat", { message, conversationId, connectionId })),

  conversations: () => body(client.get("/ai/conversations")),
  conversation: (conversationId) => body(client.get(`/ai/conversations/${conversationId}`)),
  rename: (conversationId, title) => body(client.put(`/ai/conversations/${conversationId}`, { title })),
  remove: (conversationId) => body(client.delete(`/ai/conversations/${conversationId}`)),
  // Several at once, or the lot. A POST because a body on a DELETE is widely
  // dropped on the way.
  removeMany: (ids) => body(client.post("/ai/conversations/delete", { ids })),
  clearAll: () => body(client.post("/ai/conversations/delete", { all: true })),
  // The link to hand somebody, minted and revoked.
  publish: (conversationId) => body(client.post(`/ai/conversations/${conversationId}/share`)),
  unpublish: (conversationId) => body(client.delete(`/ai/conversations/${conversationId}/share`)),
  // A thread read from its link, with no session at all.
  shared: (token) => body(client.get(`/public/ai/conversations/${token}`)),
  exportConversation: (conversationId, format = "pdf") =>
    client
      .get(`/ai/conversations/${conversationId}/export.${format}`, { responseType: "blob" })
      .then((response) => response.data),
  exportShared: (token, format = "pdf") =>
    client
      .get(`/public/ai/conversations/${token}/export.${format}`, { responseType: "blob" })
      .then((response) => response.data),
};

export const training = {
  list: () => body(client.get("/training")),
  get: (assignmentId) => body(client.get(`/training/${assignmentId}`)),
  setStatus: (assignmentId, status) => body(client.put(`/training/${assignmentId}/status`, { status })),
  logSet: (assignmentId, setId, payload) => body(client.put(`/training/${assignmentId}/sets/${setId}/log`, payload)),
  // A whole session ticked off at once, keeping whatever each set already
  // carried (see the API's ProgramStore.SetDayDone).
  setDayDone: (assignmentId, dayId, done) => body(client.put(`/training/${assignmentId}/days/${dayId}/log`, { done })),
  // The block with this member's feedback on it, whole or one week - the sheet
  // a lifter sends their coach, and the one a coach reads away from the app.
  export: (assignmentId, { format = "pdf", week, day, locale } = {}) =>
    client
      .get(`/training/${assignmentId}/export.${format}`, {
        params: { ...(week ? { week } : {}), ...(day ? { day } : {}), ...(locale ? { locale } : {}) },
        responseType: "blob",
      })
      .then((response) => response.data),
  // What the AI said about one finished session, generated on demand and
  // kept. Deleting it is how a member asks for another reading.
  generateFeedback: (assignmentId, dayId, locale, connectionId) =>
    body(client.post(`/training/${assignmentId}/days/${dayId}/feedback`, null, {
      params: { ...(locale ? { locale } : {}), ...(connectionId ? { connectionId } : {}) },
    })),
  // One reading, by its own id: a session holds as many as were asked for.
  deleteFeedback: (assignmentId, dayId, feedbackId) =>
    body(client.delete(`/training/${assignmentId}/days/${dayId}/feedback/${feedbackId}`)),
};

// --- social ---

export const social = {
  feed: (page = 0) => body(client.get("/posts", { params: { page, size: PAGE_SIZE } })),
  discover: (page = 0) => body(client.get("/posts/discover", { params: { page, size: PAGE_SIZE } })),
  createPost: (payload) => body(client.post("/posts", payload)),
  updatePost: (postId, payload) => body(client.put(`/posts/${postId}`, payload)),
  removePost: (postId) => body(client.delete(`/posts/${postId}`)),
  like: (postId) => body(client.post(`/posts/${postId}/like`)),
  unlike: (postId) => body(client.delete(`/posts/${postId}/like`)),
  comments: (postId, page = 0) => body(client.get(`/posts/${postId}/comments`, { params: { page, size: PAGE_SIZE } })),
  addComment: (postId, content) => body(client.post(`/posts/${postId}/comments`, { content })),
  updateComment: (postId, commentId, content) =>
    body(client.put(`/posts/${postId}/comments/${commentId}`, { content })),
  removeComment: (postId, commentId) => body(client.delete(`/posts/${postId}/comments/${commentId}`)),
  report: (payload) => body(client.post("/reports", payload)),
};

export const profiles = {
  get: (handle) => body(client.get(`/profiles/${handle}`)),
  posts: (handle, page = 0) => body(client.get(`/profiles/${handle}/posts`, { params: { page, size: PAGE_SIZE } })),
  follows: (handle, direction) => body(client.get(`/profiles/${handle}/follows`, { params: { direction } })),
  follow: (handle) => body(client.post(`/profiles/${handle}/follow`)),
  unfollow: (handle) => body(client.delete(`/profiles/${handle}/follow`)),
};

// --- search and invitations ---

export const search = {
  // Readable logged out, but a logged-out caller only ever gets public
  // profiles: the visibility rules run inside the API's query.
  members: (params) => body(client.get("/search/members", { params })),
};

export const invitations = {
  mine: () => body(client.get("/users/me/invitations")),
  accept: (invitationId) => body(client.post(`/users/me/invitations/${invitationId}/accept`)),
  decline: (invitationId) => body(client.post(`/users/me/invitations/${invitationId}/decline`)),
  forClub: (clubId) => body(client.get(`/clubs/${clubId}/invitations`)),
  invite: (clubId, payload) => body(client.post(`/clubs/${clubId}/invitations`, payload)),
  withdraw: (clubId, invitationId) => body(client.delete(`/clubs/${clubId}/invitations/${invitationId}`)),
};

// --- private messages and the block list ---

export const messages = {
  conversations: () => body(client.get("/messages")),
  unread: () => body(client.get("/messages/unread")),
  // Addressed by who, not by conversation id: there is exactly one thread per
  // pair, so the id is the API's business rather than the client's.
  thread: (userId) => body(client.get(`/messages/with/${userId}`)),
  // The payload carries text, pictures and a voice message's URL - the API
  // derives the link from the text, as it does for a post.
  send: (userId, payload) => body(client.post(`/messages/with/${userId}`, payload)),
  // Its sender may take back what they wrote; a superadmin may remove
  // anything. The API decides, and reports it as `deletable` on each message.
  remove: (messageId) => body(client.delete(`/messages/${messageId}`)),
};

export const blocks = {
  list: () => body(client.get("/blocks")),
  block: (userId) => body(client.post(`/blocks/${userId}`)),
  unblock: (userId) => body(client.delete(`/blocks/${userId}`)),
};

// --- media, events and the calendar feed ---

// How long an upload is allowed to take, in milliseconds.
//
// Generous on purpose: this is a video of a set going to somebody's own bucket
// over whatever connection a gym has, and the request is not finished until
// the API has forwarded every byte on to S3 or Drive. Ten minutes is far past
// any real upload and still short enough that a dead connection eventually
// reports something rather than spinning forever.
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

// The powerlifting calculator. Scored server-side (internal/strength): three
// fitted polynomials in two languages would be two answers to the same
// question, and nobody would notice which had drifted.
export const strength = {
  // Readable logged out - the calculator is the page somebody opens before
  // they have an account.
  score: (payload) => body(client.post("/strength/score", payload)),
  // The form, pre-filled from the caller's profile and recorded maxes. An
  // anonymous caller gets an empty one back.
  defaults: () => body(client.get("/strength/defaults")),
};

export const media = {
  // Uploads to the member's *own* bucket and returns its public URL. 405 means
  // they haven't configured one, which is what the composer toasts.
  uploadVideo: (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return body(
      client.post("/media/videos", form, {
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (event) =>
          onProgress?.(event.total ? Math.round((event.loaded * 100) / event.total) : 0),
      })
    );
  },
  // Same storage, same 405 when none is configured. Kept separate from the
  // video upload because the accepted types and the size cap differ.
  uploadAudio: (blob, filename) => {
    const form = new FormData();
    form.append("file", blob, filename);
    return body(client.post("/media/audio", form, { timeout: UPLOAD_TIMEOUT_MS }));
  },
  // A playback link for an object in a private bucket.
  //
  // The API serves those itself, and will not do so without knowing who is
  // asking - which a <video> tag cannot tell it. So the address in the post is
  // exchanged here, with the session, for one carrying a short-lived
  // signature, and that is what the player is given.
  link: async (url) => {
    const { url: signed } = await body(client.get(`${url.replace(/^.*\/v1/, "")}/link`));
    return signed;
  },
  // The member's own targets, in priority order: an upload goes to every one
  // of them, and the link in the post comes from the first.
  storages: () => body(client.get("/users/me/storages")),
  addStorage: (payload) => body(client.post("/users/me/storages", payload)),
  setStorage: (storageId, payload) => body(client.put(`/users/me/storages/${storageId}`, payload)),
  clearStorage: (storageId) => body(client.delete(`/users/me/storages/${storageId}`)),
  reorderStorages: (storageIds) => body(client.put("/users/me/storages/order", { storageIds })),
  // Everywhere this member may upload: their own bucket, and the ones others
  // shared with them.
  usableStorages: () => body(client.get("/storages")),
  // Who may use one of my targets, and what they may do with it.
  shares: (storageId) => body(client.get(`/users/me/storages/${storageId}/shares`)),
  share: (storageId, userId, role) =>
    body(client.post(`/users/me/storages/${storageId}/shares`, { userId, role })),
  unshare: (storageId, userId) =>
    body(client.delete(`/users/me/storages/${storageId}/shares/${userId}`)),
};

export const events = {
  // Readable logged out; what comes back still widens for a member, whose own
  // clubs' dates join the public ones.
  list: (params) => body(client.get("/events", { params })),
  get: (eventId) => body(client.get(`/events/${eventId}`)),
  create: (payload) => body(client.post("/events", payload)),
  update: (eventId, payload) => body(client.put(`/events/${eventId}`, payload)),
  remove: (eventId) => body(client.delete(`/events/${eventId}`)),
  // Uploads a federation season calendar, which the API reads into whole-day
  // competitions. clubId and visibility say where they land.
  importCalendar: (file, clubId, visibility) => {
    const form = new FormData();
    form.append("file", file);
    if (clubId) form.append("clubId", clubId);
    if (visibility) form.append("visibility", visibility);
    return body(client.post("/events/import", form));
  },
};

export const calendarFeed = {
  status: () => body(client.get("/users/me/calendar-feed")),
  enable: () => body(client.post("/users/me/calendar-feed/enable")),
  disable: () => body(client.post("/users/me/calendar-feed/disable")),
  regenerate: () => body(client.post("/users/me/calendar-feed/regenerate")),
};

// --- API keys ---

export const apiKeys = {
  list: () => body(client.get("/users/me/api-keys")),
  // The response to this call is the only place the plaintext token ever
  // exists - the API stores its hash and nothing else - so whatever the caller
  // means to do with it, it has to do now.
  create: (payload) => body(client.post("/users/me/api-keys", payload)),
  remove: (keyId) => body(client.delete(`/users/me/api-keys/${keyId}`)),
  // The token goes back to the API to be formatted, because the API is what
  // knows its own public URL. It is POSTed rather than sent as a header so no
  // reverse proxy in front of this needs a CORS exception for it.
  configQr: (key) => body(client.post("/users/me/config/qr", { key })),
  configFile: (key) => client.post("/users/me/config/file", { key }, { responseType: "blob" }).then((r) => r.data),
};

// --- the mobile build ---

export const mobileApp = {
  // Public: you need the app before you have an account.
  get: () => body(client.get("/mobile-app")),
};

// --- programs shared publicly ---

export const publicPosts = {
  // Readable with no account, and only ever a post published to everybody -
  // the API's own visibility predicate decides, not this call.
  get: (postId) => body(client.get(`/public/posts/${postId}`)),
};

export const publicPrograms = {
  get: (programId) => body(client.get(`/public/programs/${programId}`)),
  // The prescription as a document, without the weights: a reader outside the
  // club has no maxes to resolve them against (see the API's
  // ExportPublicPDF).
  export: (programId, { format = "pdf", locale } = {}) =>
    client
      .get(`/public/programs/${programId}/export.${format}`, {
        params: { ...(locale ? { locale } : {}) },
        responseType: "blob",
      })
      .then((response) => response.data),
};

// --- contact ---

export const contact = {
  // Forwarded by the API to CWCloud's contact-request endpoint; name and
  // firstname are optional.
  send: (payload) => body(client.post("/contact", payload)),
};

// --- administration ---

export const admin = {
  stats: () => body(client.get("/admin/stats")),
  users: () => body(client.get("/admin/users")),
  updateUser: (userId, payload) => body(client.put(`/admin/users/${userId}`, payload)),
  removeUser: (userId) => body(client.delete(`/admin/users/${userId}`)),
  clearMfa: (userId) => body(client.delete(`/admin/users/${userId}/mfa`)),
  userIps: (userId) => body(client.get(`/admin/users/${userId}/ips`)),
  coachRequests: () => body(client.get("/admin/coach-requests")),
  decideCoachRequest: (userId, status, motive) =>
    body(client.put(`/admin/coach-requests/${userId}`, { status, motive })),
  reports: (status, page = 0) =>
    body(client.get("/admin/reports", { params: { status: status || undefined, page, size: PAGE_SIZE } })),
  resolveReport: (reportId, status, deleteTarget) =>
    body(client.put(`/admin/reports/${reportId}`, { status, deleteTarget })),
};
